import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import { getArticlesByType } from "@/utils/data";

const PAGE_SIZE = 4;

const tagOptions = ["全部", "深度解析", "符号解读", "影史脉络", "视听语言", "声音设计", "结尾解读"];
const sortOptions = [
  { value: "newest", label: "按时间排序" },
  { value: "oldest", label: "按时间正序" },
];

export default function PlotAnalysisPage() {
  useSeo({
    title: "剧情解析专题 - 蜜桃成熟时1997影视内容站",
    description:
      "聚焦《蜜桃成熟时1997》及九十年代经典亚洲电影的剧情解析专题，从时间结构、符号隐喻与视听语言多个角度，带你重新读懂这些作品。",
    keywords: "剧情解析,电影解析,蜜桃成熟时1997解析,符号解读,影史脉络",
  });

  const [tag, setTag] = useState("全部");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);

  const base = getArticlesByType("analysis");

  const filtered = useMemo(() => {
    const list = base.filter((item) => tag === "全部" || item.tag === tag);
    return [...list].sort((a, b) =>
      sort === "newest" ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date),
    );
  }, [base, tag, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 pt-8 md:pt-10">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "剧情解析" },
          ]}
        />
      </div>

      <section className="w-full px-4 md:px-8 pt-6 pb-10 md:pb-12">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
          <i className="ri-lightbulb-line"></i>
          专题策划
        </span>
        <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
          剧情解析专题
        </h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          影像的魅力，往往藏在那些不被说出口的细节里。本专题从时间结构、符号隐喻、视听语言与声音设计等角度，带你逐层拆解《蜜桃成熟时1997》及九十年代经典作品，让每一次重看都有新的发现。
        </p>
      </section>

      <section className="w-full px-4 md:px-8 pb-16">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex flex-wrap gap-2">
            {tagOptions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  setTag(option);
                  setPage(1);
                }}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  tag === option
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {option}
              </button>
            ))}
          </div>
          <div className="px-1 py-1 rounded-full bg-background-100 inline-flex gap-1 shrink-0">
            {sortOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setSort(option.value);
                  setPage(1);
                }}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  sort === option.value
                    ? "bg-background-50 text-foreground-950 shadow-sm"
                    : "text-foreground-600"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {paged.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {paged.map((item) => (
                <article
                  key={item.id}
                  className="group rounded-lg overflow-hidden border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
                >
                  <div className="relative w-full h-48 md:h-56 overflow-hidden bg-background-200">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} - 剧情解析`}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs">
                      {item.tag}
                    </span>
                  </div>
                  <div className="p-5 md:p-6">
                    <p className="text-xs text-foreground-400 mb-2">{item.date}</p>
                    <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 leading-snug">
                      {item.title}
                    </h2>
                    <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                      {item.excerpt}
                    </p>
                    <details className="mt-4 group/details">
                      <summary className="inline-flex items-center gap-1.5 text-sm text-primary-600 cursor-pointer select-none list-none">
                        阅读全文
                        <i className="ri-arrow-down-s-line group-open/details:rotate-180 transition-transform"></i>
                      </summary>
                      <p className="mt-3 text-sm text-foreground-700 leading-loose whitespace-pre-line">
                        {item.content}
                      </p>
                    </details>
                  </div>
                </article>
              ))}
            </div>
            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </>
        ) : (
          <div className="rounded-lg border border-background-200 bg-background-50 py-20 text-center">
            <h3 className="font-heading text-lg font-semibold text-foreground-950">
              该主题下暂无解析文章
            </h3>
            <button
              type="button"
              onClick={() => setTag("全部")}
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              查看全部解析
            </button>
          </div>
        )}

        <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-6 text-center">
          <p className="text-sm text-foreground-600">
            想找更多影片资料？前往
            <Link to="/category" className="text-primary-600 hover:underline mx-1">
              电影分类
            </Link>
            浏览九十年代经典作品。
          </p>
        </div>
      </section>
    </div>
  );
}